import { Icon } from '../ui/Icon';
import { Badge, Button, Card, ErrorPanel, KpiValue, Skeleton } from '../ui/primitives';
import { cx } from '../ui/primitives/cx';
import { canOpenPos } from '../../layouts/navConfig';
import type { UserRole } from '../../types/auth';
import type { HomeFinancial, OwnerOverview } from '../../types/dashboard';
import { breakEvenView, monthStripView, resultView, todaySalesView, weekBarsView, type BreakEvenView, type ResultView, type TodaySalesView, type WeekBarsView } from '../../utils/inicioView';
import { overviewBlockView } from '../../utils/ownerOverview';
import { reportHash } from '../../utils/reportsRoute';
import { CardHead, InlineError, Money } from './InicioParts';

/** Cómo va la otra llamada del Inicio (el resumen): de ahí salen la semana y el punto de equilibrio. */
export type SummaryState = 'loading' | 'error' | 'ready';

interface InicioPulseProps {
  overview: OwnerOverview | null | undefined;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  financial: HomeFinancial | null;
  summaryState: SummaryState;
  onRetrySummary: () => void;
  role: UserRole | undefined;
  onNavigate: (destination: string) => void;
  onOpenResult: () => void;
  onOpenBreakEven: () => void;
}

const TITLE_ID = 'inicio-pulse-title';
const TITLE = 'Así va tu negocio';

/**
 * Rediseño · Bloque C — «Así va tu negocio» (P-030, con el resumen de P-021).
 *
 * Arriba, hoy: lo vendido y la semana en barras. Abajo, el resultado estimado del mes (B0-01) y el
 * punto de equilibrio. Al pie, el mes en una línea, que lleva a Reportes.
 *
 * Dos llamadas, como antes: el bloque (owner-overview) y el resumen (home-summary). Si una falla, lo
 * que depende de la otra sigue en pie, y lo que falló se dice — con su «Reintentar» — en vez de
 * desaparecer o de pintarse en cero. Vale en los dos sentidos: si falla el bloque y el resumen
 * llegó, la semana y el punto de equilibrio —con su botón— se quedan a la vista.
 */
export function InicioPulse({ overview, loading, error, onRetry, financial, summaryState, onRetrySummary, role, onNavigate, onOpenResult, onOpenBreakEven }: InicioPulseProps) {
  const block = overviewBlockView({ loading, error, overview });

  if (block.state === 'LOADING') {
    // Esqueleto con la FORMA final: la altura no salta cuando llegan los números.
    return (
      <Card aria-busy="true" aria-labelledby={TITLE_ID} as="section" className="n3-inicio__pulse">
        <CardHead id={TITLE_ID} title={TITLE} />
        <span className="n3-visually-hidden" role="status">Cargando tus números…</span>
        <div className="n3-inicio__pulse-row">
          <div className="n3-inicio__pulse-cell n3-inicio__pulse-cell--figure n3-inicio__skeletons">
            <Skeleton width="120px" />
            <Skeleton height="46px" width="210px" />
            <Skeleton width="180px" />
            <Skeleton height="36px" width="250px" />
          </div>
          <div className="n3-inicio__pulse-cell"><Skeleton height="160px" /></div>
        </div>
        <hr className="n3-inicio__rule" />
        <div className="n3-inicio__pulse-row">
          <div className="n3-inicio__pulse-cell n3-inicio__pulse-cell--figure n3-inicio__skeletons">
            <Skeleton width="140px" />
            <Skeleton height="34px" width="140px" />
            <Skeleton width="220px" />
          </div>
          <div className="n3-inicio__pulse-cell"><Skeleton height="84px" /></div>
        </div>
        <div className="n3-inicio__pulse-foot"><Skeleton className="n3-inicio__month-skeleton" height="44px" /></div>
      </Card>
    );
  }

  if (block.state === 'ERROR') {
    return (
      <Card aria-labelledby={TITLE_ID} as="section" className="n3-inicio__pulse">
        <CardHead id={TITLE_ID} title={TITLE} />
        <ErrorPanel description="Puede ser la conexión. El resto de tu información sigue aquí." onRetry={onRetry} plain title={block.message} />
        {/* Lo que falló es el bloque. La semana y el punto de equilibrio vienen del resumen: si llegaron, se quedan. */}
        {financial && (
          <>
            <hr className="n3-inicio__rule" />
            <div className="n3-inicio__pulse-row">
              <WeekCell financial={financial} onRetrySummary={onRetrySummary} summaryState={summaryState} />
              <BreakEvenCell financial={financial} onOpen={onOpenBreakEven} onRetrySummary={onRetrySummary} role={role} summaryState={summaryState} />
            </div>
          </>
        )}
      </Card>
    );
  }

  if (block.state === 'NO_ACCESS') {
    // Un gerente sin acceso financiero: el bloque explica por qué, en vez de desaparecer.
    return (
      <Card aria-labelledby={TITLE_ID} as="section" className="n3-inicio__pulse">
        <CardHead id={TITLE_ID} title={TITLE} />
        <p className="n3-inicio__support">{block.message}</p>
      </Card>
    );
  }

  const data = overview!;
  const today = todaySalesView(data.today);
  const result = resultView(data);
  const month = monthStripView(data);

  return (
    <Card aria-labelledby={TITLE_ID} as="section" className="n3-inicio__pulse">
      <CardHead aside={<Badge>{block.scopeLabel}</Badge>} id={TITLE_ID} title={TITLE} />

      {/* Qué NO incluyen estas cifras: alcance acotado y tiendas en horarios distintos. */}
      {block.scopeNotes.length > 0 && (
        <ul className="n3-inicio__scope-notes">
          {block.scopeNotes.map((note) => <li key={note}>{note}</li>)}
        </ul>
      )}

      <div className="n3-inicio__pulse-row">
        <div className="n3-inicio__pulse-cell n3-inicio__pulse-cell--figure">
          {today && <TodayBlock canSell={canOpenPos(role)} onNavigate={onNavigate} today={today} />}
        </div>
        <WeekCell financial={financial} onRetrySummary={onRetrySummary} summaryState={summaryState} />
      </div>

      <hr className="n3-inicio__rule" />
      <div className="n3-inicio__pulse-row">
        <div className="n3-inicio__pulse-cell n3-inicio__pulse-cell--figure">
          <ResultBlock onNavigate={onNavigate} onOpen={onOpenResult} view={result} />
        </div>
        <BreakEvenCell financial={financial} onOpen={onOpenBreakEven} onRetrySummary={onRetrySummary} role={role} summaryState={summaryState} />
      </div>

      <div className="n3-inicio__pulse-foot">
        {/* Bloque F: como en P-030, «Ver reporte» abre R1 («¿Cómo va mi negocio?»), que empieza en «Este mes». */}
        <button className="n3-inicio__month" onClick={() => onNavigate(reportHash('negocio'))} type="button">
          <span aria-hidden="true" className="n3-inicio__month-icon"><Icon name="calendar" size={18} /></span>
          <span className="n3-inicio__month-text">
            {month.lead} <strong>{month.amountLabel}</strong> en ventas{month.comparison ? ` · ${month.comparison}` : ''}
          </span>
          <span className="n3-inicio__month-go">Ver reporte <Icon name="chevron-right" size={16} /></span>
        </button>
      </div>
    </Card>
  );
}

interface SummaryCellProps {
  financial: HomeFinancial | null;
  summaryState: SummaryState;
  onRetrySummary: () => void;
}

/** La semana: sale del resumen. Mientras llega, su marcador; si no llegó, se dice. */
function WeekCell({ financial, summaryState, onRetrySummary }: SummaryCellProps) {
  const week = financial ? weekBarsView(financial.week) : null;
  return (
    <div className="n3-inicio__pulse-cell">
      {week && <WeekChart view={week} />}
      {!week && summaryState === 'loading' && <Skeleton height="160px" />}
      {!week && summaryState === 'error' && <InlineError onRetry={onRetrySummary} text="No pude traer tus últimos 7 días." />}
    </div>
  );
}

/** El punto de equilibrio: sale del resumen, igual que la semana. */
function BreakEvenCell({ financial, summaryState, onRetrySummary, role, onOpen }: SummaryCellProps & { role: UserRole | undefined; onOpen: () => void }) {
  // La hoja solo la guarda el dueño: el servidor responde 403 a cualquier otro.
  const breakEven = financial ? breakEvenView(financial, role === 'OWNER') : null;
  return (
    <div className="n3-inicio__pulse-cell">
      {breakEven && <BreakEvenBlock onOpen={onOpen} view={breakEven} />}
      {!breakEven && summaryState === 'loading' && <Skeleton height="84px" />}
      {!breakEven && summaryState === 'error' && <InlineError onRetry={onRetrySummary} text="No pude traer tu punto de equilibrio." />}
    </div>
  );
}

function FigureLabel({ icon, tone, children }: { icon: 'bars' | 'coins'; tone: 'success' | 'highlight'; children: string }) {
  return (
    <>
      <span aria-hidden="true" className={cx('n3-inicio__dot', `n3-inicio__dot--${tone}`)}><Icon name={icon} size={13} /></span>
      <span>{children}</span>
    </>
  );
}

function TodayBlock({ today, canSell, onNavigate }: { today: TodaySalesView; canSell: boolean; onNavigate: (destination: string) => void }) {
  if (today.noSalesYet) {
    return (
      <div className="n3-inicio__empty-today">
        <span aria-hidden="true" className="n3-inicio__tile n3-inicio__tile--info n3-inicio__tile--lg"><Icon name="shift" size={24} /></span>
        <h3 className="n3-inicio__empty-today-title">Hoy todavía no hay ventas registradas.</h3>
        <p className="n3-inicio__support">Cuando entre la primera, aquí vas a ver cómo va tu día.</p>
        {canSell && (
          <Button iconStart={<Icon name="sell" size={18} />} onClick={() => onNavigate('vender')} variant="tonal">Ir a Vender</Button>
        )}
      </div>
    );
  }
  const { compare } = today;
  return (
    <>
      <KpiValue hint={today.salesLabel} label={<FigureLabel icon="bars" tone="success">Ventas de hoy</FigureLabel>} size="xl" value={<Money label={today.amountLabel} />} />
      <p className={cx('n3-inicio__compare', `n3-inicio__compare--${compare.tone}`)}>
        {compare.tone === 'up' && <span aria-hidden="true" className="n3-inicio__compare-icon"><Icon name="trend-up" size={16} /></span>}
        {compare.tone === 'down' && <span aria-hidden="true" className="n3-inicio__compare-icon"><Icon name="trend-down" size={16} /></span>}
        <span>{compare.lead}{compare.strong && <strong>{compare.strong}</strong>}{compare.tail}</span>
      </p>
    </>
  );
}

/**
 * La semana en barras. Cada día se lee con su importe; la barra solo lo acompaña. Un día en negativo
 * no tiene barra —igual que uno en $0—, así que su importe se escribe debajo: sin eso, a la vista no
 * se distinguirían.
 */
function WeekChart({ view }: { view: WeekBarsView }) {
  return (
    <div className="n3-inicio__chart">
      <p className="n3-inicio__chart-head"><span>Últimos 7 días</span><span className="n3-inicio__chart-status">{view.status}</span></p>
      <ol aria-label="Ventas de los últimos 7 días" className="n3-inicio__chart-days">
        {view.bars.map((bar) => (
          <li className="n3-inicio__chart-day" key={bar.label + bar.description} title={bar.description}>
            <span aria-hidden="true" className="n3-inicio__chart-track">
              <span
                className={cx('n3-inicio__chart-bar', bar.isToday && 'n3-inicio__chart-bar--today', bar.isPending && 'n3-inicio__chart-bar--pending')}
                style={bar.isPending ? undefined : { height: `${bar.heightPercent}%` }}
              />
            </span>
            <span aria-hidden="true" className={cx('n3-inicio__chart-label', bar.isToday && 'n3-inicio__chart-label--today')}>{bar.label}</span>
            <span className="n3-visually-hidden">{bar.description}</span>
          </li>
        ))}
      </ol>
      {view.negativeNote && (
        <p className="n3-inicio__chart-note"><Icon name="info" size={14} /><span>{view.negativeNote}</span></p>
      )}
    </div>
  );
}

/**
 * El resultado estimado, con la certeza que le da su sobre (B0-01): confiable y estimado son la cifra
 * del bloque; incompleto se dice con el número en chico y una advertencia; y cuando no se puede
 * calcular no hay número — hay qué falta y el atajo para arreglarlo.
 */
function ResultBlock({ view, onOpen, onNavigate }: { view: ResultView; onOpen: () => void; onNavigate: (destination: string) => void }) {
  if (!view.isHeadline) {
    return (
      <div className="n3-inicio__warning-box">
        <p className="n3-inicio__warning-box-label"><Icon name="info" size={16} /><span>{view.label} · {view.periodLabel}</span></p>
        {view.amountLabel && <p className="n3-inicio__warning-box-amount">{view.amountLabel} aprox.</p>}
        {view.message && <p className="n3-inicio__warning-box-title">{view.message}</p>}
        {/* P-072: parte del costo es aproximado (lo dice el servidor); misma zona explicativa, sin rediseñar. */}
        {view.approximateNote && <p className="n3-inicio__support n3-inicio__approximate-note">{view.approximateNote}</p>}
        {view.coverageText && <p className="n3-inicio__support">{view.coverageText}</p>}
        {view.cta && <Button onClick={() => onNavigate(view.cta!.destination)} variant="primary">{view.cta.label}</Button>}
      </div>
    );
  }
  return (
    <>
      <KpiValue
        hint={view.periodLabel}
        label={<FigureLabel icon="coins" tone="highlight">{view.label}</FigureLabel>}
        size="lg"
        value={<><Money label={view.amountLabel!} /> <span className="n3-inicio__approx">aprox.</span></>}
      />
      {view.badge && <p className="n3-inicio__estimate"><Badge tone="info">{view.badge}</Badge><span>{view.message}</span></p>}
      {view.approximateNote && <p className="n3-inicio__support n3-inicio__approximate-note">{view.approximateNote}</p>}
      <Button className="n3-inicio__link n3-inicio__how" iconStart={<Icon name="info" size={16} />} onClick={onOpen} variant="ghost">Ver cómo se calcula</Button>
    </>
  );
}

function BreakEvenBlock({ view, onOpen }: { view: BreakEvenView; onOpen: () => void }) {
  return (
    <div className={cx('n3-inicio__be', `n3-inicio__be--${view.state}`)}>
      <div className="n3-inicio__be-top">
        <span aria-hidden="true" className="n3-inicio__be-icon">
          <Icon name={view.state === 'ok' ? 'check' : view.state === 'short' ? 'arrow-up' : 'plus'} size={14} />
        </span>
        <div className="n3-inicio__be-text">
          <p className="n3-inicio__be-label">Punto de equilibrio</p>
          <p className="n3-inicio__be-status">
            {view.targetLabel && <strong className="n3-inicio__be-target">{view.targetLabel}</strong>}
            <span>{view.message}</span>
          </p>
        </div>
        {view.actionLabel && <Button className="n3-inicio__be-action" onClick={onOpen}>{view.actionLabel}</Button>}
      </div>
      <div className="n3-inicio__be-bar">
        <div
          aria-label="Avance hacia el punto de equilibrio de hoy"
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={view.progressPercent}
          aria-valuetext={view.targetLabel ? view.message : 'Sin configurar'}
          className="n3-inicio__be-track"
          role="progressbar"
        >
          <span className="n3-inicio__be-fill" style={{ width: `${view.progressPercent}%` }} />
        </div>
        <span aria-hidden="true" className="n3-inicio__be-end">{view.state === 'ok' && <Icon name="check" size={10} />}</span>
      </div>
    </div>
  );
}
